import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

export function Login() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) { toast.success("Welcome back"); nav(next); }
  };

  return (
    <AuthShell title="Sign In" side="Welcome back to the House of Barique.">
      <form onSubmit={submit} className="space-y-6">
        <Input label="Email" testid="login-email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required />
        <Input label="Password" testid="login-password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required />
        {error && <p className="text-xs text-destructive">{error}</p>}
        <button data-testid="login-submit" disabled={busy} className="lux-btn-dark w-full">{busy?"…":"Sign In"}</button>
      </form>
      <div className="mt-8 flex justify-between text-xs">
        <Link to="/forgot" className="lux-link">Forgot Password</Link>
        <Link to="/signup" className="lux-link">Create Account</Link>
      </div>
    </AuthShell>
  );
}

export function Signup() {
  const { register, error } = useAuth();
  const [form, setForm] = useState({ name:"", email:"", password:"" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const setF = (k)=>(e)=>setForm({...form,[k]:e.target.value});

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const ok = await register(form.name, form.email, form.password);
    setBusy(false);
    if (ok) { toast.success("Welcome to Barique"); nav("/account"); }
  };

  return (
    <AuthShell title="Create Account" side="Join the atelier — private previews and first access.">
      <form onSubmit={submit} className="space-y-6">
        <Input label="Full Name" testid="signup-name" value={form.name} onChange={setF("name")} required />
        <Input label="Email" testid="signup-email" type="email" value={form.email} onChange={setF("email")} required />
        <Input label="Password" testid="signup-password" type="password" value={form.password} onChange={setF("password")} required minLength={6} />
        {error && <p className="text-xs text-destructive">{error}</p>}
        <button data-testid="signup-submit" disabled={busy} className="lux-btn-dark w-full">{busy?"…":"Create Account"}</button>
      </form>
      <p className="mt-8 text-xs text-center"><Link to="/login" className="lux-link">Already have an account? Sign in</Link></p>
    </AuthShell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setSent(true); toast("Check your inbox"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <AuthShell title="Reset Password" side="Enter your email — we'll send a reset link.">
      {sent ? (
        <p className="text-sm text-muted-foreground">If an account exists for <strong>{email}</strong>, a reset link has been sent.</p>
      ) : (
        <form onSubmit={submit} className="space-y-6">
          <Input label="Email" testid="forgot-email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required />
          <button className="lux-btn-dark w-full" data-testid="forgot-submit">Send Reset Link</button>
        </form>
      )}
      <p className="mt-8 text-xs text-center"><Link to="/login" className="lux-link">Back to sign in</Link></p>
    </AuthShell>
  );
}

function AuthShell({ title, side, children }) {
  const { settings } = useSite();
  const img = settings?.auth?.image;
  const tag = settings?.auth?.tagline;
  return (
    <div className="pt-24 min-h-screen grid md:grid-cols-2 bg-ivory">
      <div className="relative hidden md:block bg-ink">
        <img src={img} alt="" className="w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/10" />
        <div className="absolute bottom-14 left-14 text-ivory max-w-xs">
          <p className="overline text-ivory/70">The Maison</p>
          <p className="mt-4 font-serif text-3xl tracking-tight">{tag || side}</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md">
          <p className="overline text-muted-foreground">Account</p>
          <h1 className="mt-4 font-serif font-light tracking-tighter text-4xl md:text-5xl">{title}</h1>
          <div className="mt-10">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, testid, ...r }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}</label>
      <input className="lux-input" data-testid={testid} {...r} />
    </div>
  );
}
